import { Box, Text } from '@pancakeswap/uikit'
import Image from 'next/image'
import { ReactNode } from 'react'
import styled, { css, keyframes } from 'styled-components'
import TokenLogo from 'views/TokenPages/components/TokenLogo'
import { useTokenMeta } from '../hooks'
import { WalletProfile, holdWindow, prizeLabel, scoreHuman, scoreLegend, scoreView } from '../identity/logic'
import WalletIdentity from '../identity/WalletIdentity'
import { Display, Fluent, MONO } from '../ui'

export interface BoardRow {
  rank: number
  wallet: string
  score: string
  reward?: string | null
}

const TONE = {
  1: { main: '#F6C453', soft: 'rgba(246, 196, 83, 0.22)', edge: 'rgba(246, 196, 83, 0.6)', ink: '#2b1a00', hi: '#FFE9A6', lo: '#C98A12', label: '1st' },
  2: { main: '#C9D3E6', soft: 'rgba(201, 211, 230, 0.18)', edge: 'rgba(201, 211, 230, 0.5)', ink: '#141b2b', hi: '#F2F6FF', lo: '#8190AA', label: '2nd' },
  3: { main: '#E0955B', soft: 'rgba(224, 149, 91, 0.2)', edge: 'rgba(224, 149, 91, 0.55)', ink: '#2a1204', hi: '#FFC999', lo: '#A5622F', label: '3rd' },
} as const
const ME = 'rgba(127, 212, 232, 0.9)'
/** prize = gold, score = ice: the two numbers never share a colour, and each label wears its number's colour */
const GOLD = '#F6C453'
const GOLD_LABEL = 'rgba(246, 196, 83, 0.88)'
const ICE = '#D6F3FB'
const ICE_LABEL = 'rgba(160, 226, 242, 0.9)'
const HEAD_BG = '#0f1638'

const shine = keyframes`
  0% { transform: translateX(-120%) skewX(-18deg); }
  60%, 100% { transform: translateX(260%) skewX(-18deg); }
`

const Hero = styled.section`
  position: relative;
  isolation: isolate;
  overflow: hidden;
  margin-top: 16px;
  padding: 20px 12px 12px;
  border-radius: 28px;
  border: 1px solid rgba(180, 200, 255, 0.2);
  background:
    radial-gradient(70% 55% at 50% -8%, rgba(246, 196, 83, 0.22), transparent 70%),
    radial-gradient(60% 50% at 8% 100%, rgba(127, 212, 232, 0.14), transparent 70%),
    linear-gradient(180deg, #121a3d 0%, #0b1030 100%);
  box-shadow: 0 24px 60px rgba(5, 8, 30, 0.45), inset 0 1px 0 rgba(255, 255, 255, 0.1);
  ${({ theme }) => theme.mediaQueries.md} {
    padding: 28px 28px 20px;
  }
`
const Husky = styled.div`
  display: none;
  ${({ theme }) => theme.mediaQueries.xl} {
    display: block;
    position: absolute;
    top: 150px;
    width: 104px;
    pointer-events: none;
    &.l { left: 22px; transform: rotate(-7deg); }
    &.r { right: 22px; transform: rotate(7deg); }
    img { width: 100%; height: auto; display: block; border-radius: 14px; border: 1px solid rgba(180, 200, 255, 0.3); box-shadow: 0 14px 30px rgba(0, 0, 0, 0.5), 0 0 24px rgba(127, 212, 232, 0.22); }
  }
`
const Top = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px 16px;
  padding: 0 4px;
`
const Podium = styled.div<{ $n?: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $n }) => $n ?? 3}, minmax(0, ${({ $n }) => (($n ?? 3) === 3 ? '1fr' : '240px')}));
  justify-content: center;
  /* every slot stretches to the row; 2nd and 3rd start lower by the same step, so the twins are always the same height whatever their words */
  align-items: stretch;
  gap: 6px;
  margin-top: 20px;
  ${({ theme }) => theme.mediaQueries.md} {
    gap: 20px;
    max-width: 760px;
    margin-left: auto;
    margin-right: auto;
  }
`
const STEP = 36
const Slot = styled.div<{ $place: 1 | 2 | 3; $me: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 0;
  margin-top: ${({ $place }) => ($place === 1 ? 0 : STEP)}px;
  padding: ${({ $place }) => ($place === 1 ? '44px 2px 12px' : '16px 2px 12px')};
  border-radius: 22px;
  overflow: hidden;
  border: 1px solid ${({ $place }) => TONE[$place].edge};
  background: linear-gradient(180deg, ${({ $place }) => TONE[$place].soft} 0%, rgba(255, 255, 255, 0.03) 100%);
  box-shadow: ${({ $place, $me }) => `0 14px 34px ${TONE[$place].soft}, inset 0 1px 0 rgba(255, 255, 255, 0.16)${$me ? `, 0 0 0 2px ${ME}, 0 0 30px rgba(127, 212, 232, 0.5)` : ''}`};
  ${({ $place }) =>
    $place === 1 &&
    css`
      &::after {
        content: '';
        position: absolute;
        top: 0;
        bottom: 0;
        width: 40%;
        background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.16), transparent);
        animation: ${shine} 5s ease-in-out infinite;
        pointer-events: none;
        @media (prefers-reduced-motion: reduce) {
          animation: none;
          opacity: 0;
        }
      }
    `}
`
const MedalWrap = styled.span`
  position: relative;
  display: flex;
  justify-content: center;
  width: 100%;
  min-width: 0;
`
const MedalBadge = styled.span<{ $size: number }>`
  position: absolute;
  top: ${({ $size }) => $size - 30}px;
  left: calc(50% + ${({ $size }) => $size / 2 - 26}px);
  display: inline-flex;
  pointer-events: none;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.5));
`
const Crown = styled.span`
  position: absolute;
  left: 50%;
  top: 4px;
  transform: translateX(-50%);
  filter: drop-shadow(0 0 10px rgba(246, 196, 83, 0.7));
`
/** The two numbers of a podium place, each under its own label: the prize (gold) first, the score (ice) under it. */
const Figures = styled.dl<{ $place: 1 | 2 | 3 }>`
  margin: auto 0 0;
  padding: 10px 4px 0;
  width: 100%;
  display: grid;
  justify-items: center;
  gap: 1px;
  text-align: center;
  dt {
    font-size: 12px;
    line-height: 16px;
    font-weight: 700;
    letter-spacing: 0.01em;
    &.prize { color: ${GOLD_LABEL}; }
    &.score { color: ${ICE_LABEL}; margin-top: 8px; }
  }
  dd {
    margin: 0;
    min-width: 0;
    max-width: 100%;
  }
  dd.prize {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-family: ${MONO};
    font-variant-numeric: tabular-nums;
    font-weight: 700;
    font-size: ${({ $place }) => ($place === 1 ? 17 : 14)}px;
    line-height: 1.25;
    /* the prize is gold on every place: gold always means prize; the place's own colour stays on the slot, the ring and the medal */
    color: ${GOLD};
    text-shadow: 0 0 14px rgba(246, 196, 83, 0.35);
    small { display: none; }
    ${({ theme }) => theme.mediaQueries.md} {
      font-size: ${({ $place }) => ($place === 1 ? 20 : 16)}px;
      small { display: inline; }
    }
    /* a figure is short ("83.4M"); only the no-decimals fallback (the whole integer) is long, and it wraps inside the slot instead of running out */
    > span { max-width: 100%; overflow-wrap: anywhere; flex-wrap: wrap; justify-content: center; }
  }
  dd.score {
    display: grid;
    justify-items: center;
    b {
      font-family: ${MONO};
      font-variant-numeric: tabular-nums;
      font-weight: 600;
      font-size: 12px;
      line-height: 17px;
      color: ${ICE};
      overflow-wrap: anywhere;
      ${({ theme }) => theme.mediaQueries.md} { font-size: 13px; }
    }
    span {
      font-size: 12px;
      line-height: 16px;
      color: rgba(223, 233, 255, 0.62);
    }
  }
`
const YouTag = styled.span`
  display: inline-flex;
  align-items: center;
  height: 20px;
  padding: 0 8px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #052029;
  background: linear-gradient(135deg, #b8f1ff, #7fd4e8);
  box-shadow: 0 0 14px rgba(127, 212, 232, 0.6);
`
const SlotName = styled.div`
  width: 100%;
  min-width: 0;
  padding: 0 4px;
  /* WalletIdentity's own text: long names ellipsize inside the column, an address never does */
  > div { max-width: 100%; }
`
/** Long boards scroll inside their own frame, under a header that stays. */
const TableWrap = styled.div<{ $long: boolean }>`
  margin-top: 18px;
  ${({ $long }) =>
    $long &&
    css`
      max-height: min(72vh, 680px);
      overflow-y: auto;
      overscroll-behavior: contain;
      scrollbar-width: thin;
      scrollbar-color: rgba(180, 200, 255, 0.3) transparent;
    `}
`
/**
 * The ranks below the podium as a real table: Rank · Trader · Score · Est. prize, numbers right-aligned in tabular figures, zebra rows, the viewer's row
 * lit. From md up it is a table; on a phone each row is a two-line card (rank | trader, then score | prize) under a header with the same two lines.
 * Roles are set by hand so the table keeps its semantics when the phone layout changes the display.
 */
const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-variant-numeric: tabular-nums;
  thead, tbody { display: block; }
  thead {
    position: sticky;
    top: 0;
    z-index: 2;
    background: ${HEAD_BG};
  }
  tr {
    display: grid;
    grid-template-columns: 40px minmax(0, 1fr) auto;
    grid-template-areas: 'rank who who' 'rank score prize';
    align-items: center;
    column-gap: 10px;
    row-gap: 4px;
    padding: 10px 10px;
  }
  th, td { display: block; padding: 0; min-width: 0; text-align: left; }
  .rank { grid-area: rank; }
  .who { grid-area: who; }
  .score { grid-area: score; }
  .prize { grid-area: prize; text-align: right; }
  thead tr { padding: 6px 10px; row-gap: 0; border-bottom: 1px solid rgba(180, 200, 255, 0.16); }
  th {
    font-size: 12px;
    line-height: 18px;
    font-weight: 700;
    letter-spacing: 0.01em;
    color: rgba(223, 233, 255, 0.62);
    &.score { color: ${ICE_LABEL}; }
    &.prize { color: ${GOLD_LABEL}; }
  }
  tbody tr {
    margin-top: 6px;
    border-radius: 16px;
    border: 1px solid rgba(180, 200, 255, 0.08);
    background: rgba(255, 255, 255, 0.035);
    &:nth-child(even) { background: rgba(255, 255, 255, 0.015); }
    &[data-me='true'] {
      border-color: ${ME};
      background: linear-gradient(90deg, rgba(127, 212, 232, 0.24), rgba(127, 212, 232, 0.06));
      box-shadow: 0 0 26px rgba(127, 212, 232, 0.28), inset 0 1px 0 rgba(255, 255, 255, 0.1);
    }
  }
  td.score {
    b {
      display: block;
      font-family: ${MONO};
      font-weight: 600;
      font-size: 13px;
      line-height: 18px;
      color: ${ICE};
      overflow-wrap: anywhere;
    }
    span { display: block; font-size: 12px; line-height: 16px; color: rgba(223, 233, 255, 0.62); }
  }
  td.prize {
    font-family: ${MONO};
    font-weight: 700;
    font-size: 14px;
    line-height: 18px;
    color: ${GOLD};
    text-shadow: 0 0 12px rgba(246, 196, 83, 0.25);
    > span { justify-content: flex-end; max-width: 100%; overflow-wrap: anywhere; flex-wrap: wrap; }
  }
  ${({ theme }) => theme.mediaQueries.md} {
    display: table;
    table-layout: fixed;
    border-spacing: 0 6px;
    margin-top: -6px;
    thead { display: table-header-group; position: static; background: none; }
    tbody { display: table-row-group; }
    tr { display: table-row; padding: 0; }
    th, td { display: table-cell; vertical-align: middle; padding: 10px 14px; }
    th { position: sticky; top: 0; z-index: 2; background: ${HEAD_BG}; padding-top: 12px; padding-bottom: 8px; border-bottom: 1px solid rgba(180, 200, 255, 0.16); }
    th.rank, td.rank { width: 76px; }
    th.score, td.score { width: 30%; text-align: right; }
    th.prize, td.prize { width: 24%; }
    td.score b, td.score span { text-align: right; }
    td.score b { font-size: 14px; }
    td.prize { font-size: 16px; }
    thead tr { border: 0; }
    tbody tr { border: 0; background: none; margin: 0; }
    tbody td { background: rgba(255, 255, 255, 0.035); border-top: 1px solid rgba(180, 200, 255, 0.08); border-bottom: 1px solid rgba(180, 200, 255, 0.08); }
    tbody tr:nth-child(even) td { background: rgba(255, 255, 255, 0.015); }
    tbody td:first-child { border-left: 1px solid rgba(180, 200, 255, 0.08); border-radius: 16px 0 0 16px; }
    tbody td:last-child { border-right: 1px solid rgba(180, 200, 255, 0.08); border-radius: 0 16px 16px 0; }
    tbody tr[data-me='true'] { box-shadow: none; background: none; }
    tbody tr[data-me='true'] td { background: rgba(127, 212, 232, 0.16); border-color: ${ME}; }
    tbody tr[data-me='true'] td:first-child { background: linear-gradient(90deg, rgba(127, 212, 232, 0.3), rgba(127, 212, 232, 0.16)); }
  }
`
const RankCell = styled.span`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`
const Rank = styled.span<{ $me?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  font-family: ${MONO};
  font-weight: 700;
  font-size: 13px;
  color: ${({ $me }) => ($me ? '#052029' : 'rgba(223, 233, 255, 0.85)')};
  background: ${({ $me }) => ($me ? 'linear-gradient(135deg, #b8f1ff, #7fd4e8)' : 'rgba(255, 255, 255, 0.06)')};
  border: 1px solid ${({ $me }) => ($me ? 'transparent' : 'rgba(180, 200, 255, 0.2)')};
`
const LegendText = styled.p`
  margin: 6px 0 0;
  max-width: 640px;
  font-size: 12px;
  line-height: 18px;
  color: rgba(223, 233, 255, 0.7);
  ${({ theme }) => theme.mediaQueries.md} { font-size: 13px; line-height: 19px; }
  b {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-weight: 700;
    i { display: inline-block; width: 8px; height: 8px; border-radius: 50%; }
    &.score { color: ${ICE_LABEL}; i { background: ${ICE}; box-shadow: 0 0 8px ${ICE}; } }
    &.prize { color: ${GOLD_LABEL}; margin-left: 4px; i { background: ${GOLD}; box-shadow: 0 0 8px ${GOLD}; } }
  }
`
const Ghost = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 18px 8px;
  border-radius: 22px;
  border: 1px dashed rgba(180, 200, 255, 0.22);
  color: rgba(223, 233, 255, 0.45);
  text-align: center;
  font-size: 13px;
`

function Medal({ place, size = 56 }: { place: 1 | 2 | 3; size?: number }) {
  return <MedalBadge $size={size} role="img" aria-label={`${TONE[place].label} place`}><Fluent name={`medal${place}`} size={38} /></MedalBadge>
}

interface Ctx {
  profiles: Record<string, WalletProfile>
  me?: string
  decimals?: number
  symbol?: string
  type: string
  rewardToken?: string
  windowSeconds: number
  priceUsd?: number | null
  prizeName: string
}

/** "50.6M" and the token's own logo: the prize as the eye wants it. The whole amount is in the title. */
function PrizeText({ raw, token, size = 18 }: { raw?: string | null; token?: string; size?: number }) {
  const meta = useTokenMeta(token, true)
  if (!raw) return <span style={{ opacity: 0.5 }}>—</span>
  const v = scoreView(raw, meta.decimals, meta.symbol, 'VOLUME')
  return (
    <span title={`${v.full} ${v.unit}`} data-testid="v3-prize-rank" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <TokenLogo address={token} symbol={meta.symbol} size={size} />
      {v.figure}
      <small style={{ fontSize: 'max(0.72em, 12px)', opacity: 0.8 }}>{v.unit}</small>
    </span>
  )
}

const human = (r: BoardRow, c: Ctx) => scoreHuman(r.score, c.decimals, c.symbol, c.type, { windowSeconds: c.windowSeconds, priceUsd: c.priceUsd })

function PodiumSlot({ r, place, c }: { r: BoardRow; place: 1 | 2 | 3; c: Ctx }) {
  const mine = c.me === r.wallet
  const h = human(r, c)
  const size = place === 1 ? 72 : 56
  return (
    <Slot $place={place} $me={mine} data-testid="v3-podium-slot" data-place={place} data-me={mine ? 'true' : undefined}>
      {place === 1 && <Crown aria-hidden="true"><Fluent name="crown" size={38} /></Crown>}
      <MedalWrap>
        <SlotName>
          <WalletIdentity stack size={size} ring={TONE[place].main} wallet={r.wallet} profile={c.profiles[r.wallet]} />
        </SlotName>
        <Medal place={place} size={size} />
      </MedalWrap>
      {mine && <Box mt="6px"><YouTag>You</YouTag></Box>}
      <Figures $place={place}>
        <dt className="prize" data-testid="v3-prize-label">{c.prizeName}</dt>
        <dd className="prize"><PrizeText raw={r.reward} token={c.rewardToken} size={place === 1 ? 24 : 20} /></dd>
        <dt className="score" data-testid="v3-score-label">Score</dt>
        <dd className="score" title={h.tip} data-testid="v3-score" data-raw={r.score}>
          <b>{h.main}</b>
          <span>{h.sub}</span>
        </dd>
      </Figures>
    </Slot>
  )
}

/**
 * The leaderboard as the first thing on the page: a podium for the top three (rank colours, medal, avatar or profile name, prize and score, each under
 * its label), the rest in a table with the same labels (Rank · Trader · Score · Est. prize), the viewer's own place lit in both. A legend under the
 * title says how this type scores and what the prize is. Every avatar and name opens the wallet's CADINU profile (/u/<address>).
 */
export default function Leaderboard({ rows, title, status, phase, kind, rewardToken, rewardAmount, winners, projectToken, type, me, profiles, empty, startTime, endTime, publishedAt, priceUsd }: {
  rows: BoardRow[]
  title: string
  status: ReactNode
  phase: string
  /** the board's report kind: provisional, candidate or final */
  kind?: string | null
  rewardToken?: string
  rewardAmount?: string
  winners?: number
  projectToken?: string
  type: string
  me?: string
  profiles: Record<string, WalletProfile>
  empty: string
  startTime: number
  endTime: number
  publishedAt?: string | null
  /** the project token's price, for a volume score in dollars */
  priceUsd?: number | null
}) {
  const project = useTokenMeta(projectToken, true)
  const reward = useTokenMeta(rewardToken, true)
  const windowSeconds = holdWindow({ start: startTime, end: endTime, publishedAt, kind })
  const prizeName = prizeLabel(kind)
  const ctx: Ctx = { profiles, me, decimals: project.decimals, symbol: project.symbol, type, rewardToken, windowSeconds, priceUsd, prizeName }
  const pool = rewardAmount ? scoreView(rewardAmount, reward.decimals, reward.symbol, 'VOLUME') : undefined
  const legend = scoreLegend(type, { symbol: project.symbol, winners, pool: pool && reward.decimals !== undefined ? `${pool.figure} ${pool.unit}` : undefined, final: kind === 'final' })
  const top = rows.filter((r) => r.rank <= 3)
  const rest = rows.filter((r) => r.rank > 3)
  // classic podium order: 2nd, 1st, 3rd
  const order = [2, 1, 3].map((p) => top.find((r) => r.rank === p)).filter(Boolean) as BoardRow[]
  return (
    <Hero data-testid="v3-board" data-phase={phase} data-kind={kind ?? undefined}>
      <Husky className="l" aria-hidden="true"><Image src="/images/brand/nft/cowboy.webp" alt="" width={104} height={146} unoptimized /></Husky>
      <Husky className="r" aria-hidden="true"><Image src="/images/brand/nft/futuristic.webp" alt="" width={104} height={146} unoptimized /></Husky>
      <Top>
        <Box style={{ display: 'flex', alignItems: 'flex-start', gap: 12, minWidth: 0 }}>
          <Fluent name="trophy" size={48} style={{ filter: 'drop-shadow(0 6px 14px rgba(246,196,83,.5))' }} />
          <Box style={{ minWidth: 0 }}>
            <Display bold fontSize="24px" lineHeight="1.1" color="#f4f6ff">{title}</Display>
            <LegendText data-testid="v3-legend" data-type={type}>
              <b className="score"><i aria-hidden="true" />Score</b> {legend.score} <b className="prize"><i aria-hidden="true" />{prizeName}</b> {legend.prize}
            </LegendText>
          </Box>
        </Box>
        <Box>{status}</Box>
      </Top>

      {!rows.length && (
        <Podium data-testid="v3-podium-empty">
          {[2, 1, 3].map((p) => (
            <Ghost key={p}><Fluent name={`medal${p}`} size={44} style={{ opacity: 0.55 }} /><span>{p === 1 ? 'First place is open' : 'Open'}</span></Ghost>
          ))}
        </Podium>
      )}
      {!rows.length && <Text mt="12px" textAlign="center" style={{ color: 'rgba(223,233,255,.7)' }}>{empty}</Text>}

      {order.length > 0 && (
        <Podium $n={order.length} data-testid="v3-podium">
          {order.map((r) => <PodiumSlot key={r.wallet} r={r} place={r.rank as 1 | 2 | 3} c={ctx} />)}
        </Podium>
      )}

      {rest.length > 0 && (
        <TableWrap $long={rest.length > 10} data-testid="v3-table-wrap" data-long={rest.length > 10 ? 'true' : undefined}>
          <Table role="table" aria-label={`${title}: ranks 4 to ${rest[rest.length - 1].rank}`} data-testid="v3-leaderboard">
            {/* the phone layout sets display on the table parts, which drops their implicit roles: they are said explicitly */}
            {/* eslint-disable-next-line jsx-a11y/no-redundant-roles */}
            <thead role="rowgroup">
              <tr role="row" data-testid="v3-board-head">
                <th role="columnheader" scope="col" className="rank">Rank</th>
                <th role="columnheader" scope="col" className="who">Trader</th>
                <th role="columnheader" scope="col" className="score">Score</th>
                <th role="columnheader" scope="col" className="prize">{prizeName}</th>
              </tr>
            </thead>
            {/* eslint-disable-next-line jsx-a11y/no-redundant-roles */}
            <tbody role="rowgroup">
              {rest.map((r) => {
                const h = human(r, ctx)
                const mine = me === r.wallet
                return (
                  <tr role="row" key={r.wallet} data-testid="v3-row" data-rank={r.rank} data-me={mine ? 'true' : undefined}>
                    <td role="cell" className="rank">
                      <RankCell><Rank $me={mine} aria-label={`Rank ${r.rank}`}>{r.rank}</Rank>{mine && <YouTag>You</YouTag>}</RankCell>
                    </td>
                    <td role="cell" className="who"><WalletIdentity wallet={r.wallet} profile={profiles[r.wallet]} /></td>
                    <td role="cell" className="score" title={h.tip} data-testid="v3-score" data-raw={r.score}>
                      <b>{h.main}</b>
                      <span>{h.sub}</span>
                    </td>
                    <td role="cell" className="prize"><PrizeText raw={r.reward} token={rewardToken} size={18} /></td>
                  </tr>
                )
              })}
            </tbody>
          </Table>
        </TableWrap>
      )}
    </Hero>
  )
}
